import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';

import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import PhoneInput from 'react-phone-input-2';

import { api } from '../../api/context';
import toast from '../../utils/toast';
import date from '../../utils/date';
import urls from '../../utils/urls';
import wizard from '../../utils/wizard';
import Base from '../Base';
import styles from './Signup.module.scss';

const Signup = () => {

  const [ loading, setLoading ] = useState(false);
  const [ invitationEmail, setInvitationEmail ] = useState('');

  const [ ptkn ] = urls.useParam('ptkn');

  const navigate = useNavigate();

  useEffect(() => {
    if (!ptkn) {
      return;
    }

    api().v1.auth.signup.premium_invitation
      .obtain({ token: ptkn })
      .then(res => {
        if (res.status === 200 && res.body && res.body.email) {
          setInvitationEmail(res.body.email);
        } else {
          toast.show(toast.KIND.ERROR,
            (res.body && res.body.detail) ||
            'This invitation link is invalid or has already been used.');
        }
      })
      .catch(err => {
        toast.show(toast.KIND.WARNING, err);
      });
  }, [ptkn]);

  const int = value => parseInt(value, 10);

  return (
    <Base title='Sign Up'>
      <div className={styles.root}>
        <Formik
          enableReinitialize={true}
          initialValues={{
            phone: '',
            email: invitationEmail,
            password: '',
            birth_year: '',
            birth_month: '',
          }}
          validationSchema={yup.object({
            phone: yup.string()
              .required('Phone required'),
            email: yup.string()
              .email('Invalid email address')
              .required('Email required'),
            password: yup.string()
              .required('Password required'),
            birth_year: yup.number()
              .required('Birth year required'),
            birth_month: yup.number()
              .when('birth_year', {
                is: (value) => int(value) === date.MAX_BIRTH_YEAR,
                then: () => yup.number()
                  .required('Birth month required')
                  .test(
                    '_', 'You must be 18 years old',
                    (value) => int(value) < date.CURRENT_MONTH,
                  ),
              })
              .nullable(),
          })}
          onSubmit={(values, actions) => {

            if (loading) {
              return;
            }

            setLoading(true);

            api().v1.auth.signup
              .post({
                phone: values.phone,
                email: values.email,
                password: values.password,
                birth_year: int(values.birth_year),
                birth_month: (
                  (int(values.birth_year) === date.MAX_BIRTH_YEAR)
                  ? int(values.birth_month)
                  : undefined
                ),
              })
              .then(res => {
                setLoading(false);

                if (res.status === 200) {
                  actions.resetForm();

                  wizard.markSignup();

                  window._PASSWORD = values.password;
                  navigate(`/wizard/email?email=${encodeURIComponent(values.email)}`);
                }

                if (res.status === 400) {
                  toast.show(toast.KIND.ERROR, res.body);
                }
              })
              .catch(err => {
                setLoading(false);
                toast.show(toast.KIND.WARNING, err);
              });

          }} >

          {
            ({ values, handleChange }) => (

              <Form>
                <div>
                  <PhoneInput
                    className={styles.phone}
                    autoFocus={true}
                    aria-label='phone'
                    inputProps={{ name: 'phone' }}
                    disabled={loading}

                    autoFormat={true}
                    countryCodeEditable={false}
                    country={'us'}
                    disableDropdown={true}
                    specialLabel={'Mobile Phone'}

                    onChange={(value) => {
                      handleChange({
                        target: {
                          name: 'phone',
                          value: `+${value}`,
                        }
                      });
                    }}
                  />
                  <ErrorMessage
                    name='phone'
                    component='div'
                  />
                </div>

                <div>
                  <Field
                    aria-label='email'
                    placeholder='Email'
                    name='email'
                    type='email'
                    disabled={loading || !!ptkn}
                    readOnly={!!ptkn}
                  />
                  <ErrorMessage
                    name='email'
                    component='div'
                  />
                </div>

                <div>
                  <Field
                    aria-label='password'
                    placeholder='Password'
                    name='password'
                    type='password'
                    disabled={loading}
                  />
                  <ErrorMessage
                    name='password'
                    component='div'
                  />
                </div>

                <div>
                  <Field
                    aria-label='birth year'
                    name='birth_year'
                    as='select'
                    disabled={loading}>

                    <option
                      value=''
                      label='Birth Year'
                      disabled
                    />

                    {
                      date.BIRTH_YEAR.map(value => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))
                    }

                  </Field>

                  <ErrorMessage
                    name='birth_year'
                    component='div'
                  />
                </div>

                {
                  (int(values.birth_year) === date.MAX_BIRTH_YEAR) && (
                    <div>
                      <Field
                        aria-label='birth month'
                        name='birth_month'
                        as='select'
                        disabled={loading}>

                        <option
                          value=''
                          label='Birth Month'
                          disabled
                        />

                        {
                          date.BIRTH_MONTH.map(item => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))
                        }

                      </Field>

                      <ErrorMessage
                        name='birth_month'
                        component='div'
                      />
                    </div>
                  )
                }

                <p>
                  By submitting this form, you agree to receive recurring automated
                  promotional SMS messages. These messages may include, but are not
                  limited to, verifying your phone number, updating you on eligible
                  campaigns, providing details on campaigns including shipment, and
                  notifying you of due campaign activities. Message frequency can
                  vary. Consent is not a condition of any purchase. Text 'STOP' to
                  opt-out. Standard messaging rates may apply. You also agree to the
                  {' '}
                  <Link target='_blank' to='/terms-and-conditions'>
                    Terms of Service
                  </Link>
                  {' '}
                  and
                  {' '}
                  <Link target='_blank' to='/privacy-policy'>
                    Privacy Policy
                  </Link>
                  .
                </p>

                <button
                  aria-label='sign up'
                  type='submit'
                  disabled={loading}>

                  Sign up

                </button>
              </Form>
            )
          }

        </Formik>

      </div>
    </Base>
  );
};

export default Signup;